import { useState } from "react";
import { createRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";

import { rootRoute } from "./root.tsx";
import { SubPage } from "../components/layout.tsx";
import {
  Button,
  Empty,
  ErrorBlock,
  Img,
  Input,
  OrderStatus,
  Price,
  Spinner,
} from "../components/ui.tsx";
import {
  ordersQueryFor,
  useCancelOrder,
  useConfirmReceipt,
  useMe,
  useOrderRefunds,
  useOrders,
  useRequestRefund,
} from "../api/queries.ts";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopOrders } from "./desktop/orders.tsx";

/**
 * 订单页签。
 *
 * 🔴 **「我的」页上那四个入口原本是摆设。** 它们传的是 `?status=paid`，
 *    而这一页既没读这个参数、`validateSearch` 里也没有它 ——
 *    点「待发货」和点「全部订单」出来的是同一屏东西。
 *    有页签却不生效比没有页签更糟：用户会以为自己真的没有待发货的单。
 */
export const ORDER_FILTERS = [
  { key: "", label: "全部" },
  { key: "pending", label: "待付款" },
  { key: "paid", label: "待发货" },
  { key: "shipped", label: "待收货" },
  { key: "done", label: "已完成" },
] as const;

/**
 * 我的订单。
 *
 * 🔴 **这是二级页，必须有返回键。**
 *    以前用的是 `TabPage`（底部导航那四个一级页的壳）—— 只画了个标题，
 *    没有返回。订单列表非空时用户还能靠点商品往下走，
 *    **空态下整页只有一句"还没有订单"，人就卡在那儿了**。
 *    老板就是这么发现的。
 *
 * ⚠️ 一级页（首页/分类/购物车/我的）没有返回键是对的 —— 它们靠底部 tab
 *    互相切换，加返回反而不知道该退到哪。二级页则相反。
 */
function Orders() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/orders" }) as { status?: string };
  const { data: me, isPending: mePending } = useMe();
  const { data, isPending, error, refetch } = useOrders(
    { ...ordersQueryFor(search.status), limit: 20 },
    Boolean(me),
  );
  const confirm = useConfirmReceipt();
  const cancel = useCancelOrder();
  /** 当前展开了哪一单的退款面板。 */
  const [refundFor, setRefundFor] = useState<number | null>(null);

  if (mePending) return <SubPage title="我的订单"><Spinner /></SubPage>;
  if (!me) {
    return (
      <SubPage title="我的订单">
        <Empty title="登录后查看订单" action={{ label: "去登录", to: "/login" }} />
      </SubPage>
    );
  }
  if (isPending) return <SubPage title="我的订单"><Spinner /></SubPage>;
  if (error) return <SubPage title="我的订单"><ErrorBlock error={error} onRetry={() => refetch()} /></SubPage>;

  return (
    <SubPage title="我的订单">
      {/* ⚠️ 页签横向可滚：五个挤在 480px 里会把字压扁。 */}
      <div className="flex gap-2 overflow-x-auto border-b border-border bg-white px-3 py-2.5 no-scrollbar">
        {ORDER_FILTERS.map((f) => {
          const active = (search.status ?? "") === f.key;
          return (
            <button
              key={f.key || "all"}
              onClick={() =>
                navigate({ to: "/orders", search: { status: f.key || undefined } as never })
              }
              className={
                "shrink-0 rounded-full px-3.5 py-1.5 text-[13px] whitespace-nowrap " +
                (active ? "bg-brand-600 text-white" : "bg-secondary text-muted-foreground")
              }
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {data.items.length === 0 ? (
        <Empty
          title={search.status ? "这个状态下还没有订单" : "还没有订单"}
          action={{ label: "去逛逛", to: "/products" }}
        />
      ) : (
        data.items.map((o) => (
          <section key={o.id} className="mt-3 bg-white">
            <div className="flex items-center justify-between border-b border-neutral-100 px-4 py-2.5">
              <span className="font-mono text-xs text-ink-500">{o.orderNo}</span>
              <OrderStatus payment={o.payment} fulfillment={o.fulfillment} />
            </div>

            {o.items.map((it) => (
              <div key={it.id} className="flex gap-3 px-4 py-3">
                {/* ⚠️ 列表接口现在也返回 `imageUrl` 了 —— 以前这里是个灰方块占位。 */}
                <Img
                  src={it.imageUrl}
                  alt={it.titleSnapshot}
                  className="size-16 shrink-0 rounded object-cover"
                />
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-2 text-sm leading-snug">{it.titleSnapshot}</p>
                  <p className="mt-1 text-xs text-ink-500">
                    {Object.values(it.specsSnapshot).join(" · ")}
                  </p>
                </div>
                <div className="shrink-0 text-right text-sm">
                  <Price cents={it.unitPrice} />
                  <p className="text-xs text-ink-500">×{it.quantity}</p>
                </div>
              </div>
            ))}

            {/* 🔴 整条摘要点进去看详情 —— 物流单号只有详情页才有 */}
            <Link
              to="/orders/$id"
              params={{ id: String(o.id) }}
              className="block border-t border-neutral-100 px-4 py-2 text-xs text-brand-600"
            >
              查看详情{o.fulfillment === "shipped" ? "与物流" : ""} →
            </Link>
            <div className="flex items-center justify-between border-t border-neutral-100 px-4 py-2.5">
              <span className="text-sm text-ink-500">
                实付 <Price cents={o.payableAmount} className="text-brand-600" />
              </span>
              <div className="flex gap-2">
                {o.payment === "pending" && o.fulfillment === "pending" && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => cancel.mutate(o.id)}>
                      取消
                    </Button>
                    {/*
                      关掉页面也能回来接着付。
                      ⚠️ 文案是「去付款」不是「去支付」：这个商城默认走线下收款，
                         点进去看到的是"把钱付给谁、怎么付"，不是收银台。
                         写成"去支付"会让人以为点了就能在线上付掉。
                    */}
                    <Link to="/pay" search={{ orderIds: String(o.id) }}>
                      <Button size="sm">去付款</Button>
                    </Link>
                  </>
                )}
                {o.fulfillment === "shipped" && (
                  <Button size="sm" onClick={() => confirm.mutate(o.id)} loading={confirm.isPending}>
                    确认收货
                  </Button>
                )}
                {/*
                  🔴 付了钱就能申请售后。建材售后率不低（磕碰、色差、尺寸不符），
                     结算特意压到"确认收货 + 15 天"就是为了留出这段维权期。
                */}
                {(o.payment === "paid" || o.payment === "partial_refunded") && (
                  <Button size="sm" variant="outline" onClick={() => setRefundFor(o.id)}>
                    申请退款
                  </Button>
                )}
              </div>
            </div>
            {refundFor === o.id && (
              <RefundPanel orderId={o.id} onClose={() => setRefundFor(null)} />
            )}
          </section>
        ))
      )}
    </SubPage>
  );
}

/**
 * 申请退款的小面板。
 *
 * ⚠️ 只提交申请，不动钱 —— 平台审核通过才真的退。
 *    建材退货常要先验货，申请即退的话平台没有拦截余地。
 */
export function RefundPanel({ orderId, onClose }: { orderId: number; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const request = useRequestRefund(orderId);
  const { data: existing } = useOrderRefunds(orderId);

  const pending = existing?.find((r) => r.status === "pending");

  return (
    <div className="mx-4 mb-2 rounded-lg bg-neutral-50 p-3">
      {pending ? (
        <p className="text-xs text-ink-500">
          已有一笔待审核的退款申请（{pending.refundNo}），请等平台处理。
        </p>
      ) : request.isSuccess ? (
        <p className="text-xs text-emerald-700">已提交，平台审核后原路退回。</p>
      ) : (
        <>
          <Input
            label="退款原因"
            placeholder="如：瓷砖有磕碰 / 色差明显 / 尺寸不符"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {request.error && <ErrorBlock error={request.error} />}
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              className="flex-1"
              loading={request.isPending}
              disabled={!reason.trim()}
              onClick={() => request.mutate({ reason })}
            >
              提交申请
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose}>
              取消
            </Button>
          </div>
          {/* ⚠️ 说清楚"申请不等于已退"，否则用户会以为钱马上到账 */}
          <p className="mt-1.5 text-xs text-ink-500">
            提交后由平台审核，通过后原路退回。
          </p>
        </>
      )}
    </div>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function OrdersRoute() {
  return useIsDesktop() ? <DesktopOrders /> : <Orders />;
}

export const ordersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/orders",
  component: OrdersRoute,
  validateSearch: (s: Record<string, unknown>): { placed?: number; status?: string } => ({
    placed: s.placed ? Number(s.placed) : undefined,
    /** ⚠️ 认不出的值当成"全部" —— 用户可能点了个过期链接，不该整页报错。 */
    status: ORDER_FILTERS.some((f) => f.key && f.key === s.status)
      ? (s.status as string)
      : undefined,
  }),
});
